import React, { useEffect, useMemo, useState } from "react";

import NarrativeContent from "../components/Resultados/NarrativaContent";
import ResultadoContent from "../components/Resultados/ResultadoContent";
import TradeoffContent from "../components/Resultados/TradeoffContent";
import Select from "../components/SelectScenarios";

import WorldMap from "../assets/ethiopiaMap.png";


/* ============================================================
   PUBLIC PATHWAY MENU
   ============================================================ */

function PublicPathwayMenu({
  activeTab,
  onChangeTab,
  selectedPathwayId,
  onChangePathway,
  pathwayOptions,
  loadingPathways,
  loadingResults,
}) {
  const tabs = [
    "Pathway Summary",
    "Sector Specific Results",
    "Multiple Indicators View",
  ];

  return (
    <div className="border-b border-slate-200 bg-white">
      <div
        className="
          mx-auto
          flex
          w-full
          max-w-[1600px]
          flex-col
          gap-4
          px-6
          py-5
          md:px-10
          xl:px-14
          xl:flex-row
          xl:items-center
          xl:justify-between
        "
      >
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
          <label
            className="
              shrink-0
              text-[12px]
              font-bold
              uppercase
              tracking-[0.15em]
              text-slate-500
            "
          >
            Pathway
          </label>

          <Select
            value={selectedPathwayId || ""}
            onChange={onChangePathway}
            options={pathwayOptions}
            placeholder={
              loadingPathways
                ? "Loading pathways..."
                : pathwayOptions.length === 0
                ? "No public pathways available"
                : "Select pathway"
            }
            disabled={loadingPathways || pathwayOptions.length === 0}
            variant="ethiopia"
            className="w-full sm:w-[340px]"
          />
        </div>

        <div className="flex flex-wrap items-center gap-3 overflow-x-auto py-1">
          {tabs.map((label) => {
            const isActive = activeTab === label;

            return (
              <button
                key={label}
                type="button"
                onClick={() => onChangeTab(label)}
                className={`
                  relative
                  shrink-0
                  whitespace-nowrap
                  rounded-full
                  px-4
                  py-3
                  text-[13px]
                  font-semibold
                  transition-colors
                  duration-200
                  ${
                    isActive
                      ? "bg-[#08725b] text-white shadow-sm"
                      : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                  }
                `}
              >
                {label}
              </button>
            );
          })}
        </div>

        {loadingResults && (
          <div className="flex items-center gap-2 text-xs text-slate-400">
            <span
              className="
                inline-block
                h-3.5
                w-3.5
                animate-spin
                rounded-full
                border-2
                border-slate-200
                border-t-[#08725b]
              "
            />
            Loading results...
          </div>
        )}
      </div>
    </div>
  );
}


/* ============================================================
   PUBLIC RESULTS
   ============================================================ */

export default function PublicResultados() {
  /* ==========================================================
     MAIN TAB
     ========================================================== */

  const [activeTab, setActiveTabState] = useState(
    () =>
      localStorage.getItem("public_resultados_activeTab") ||
      "Pathway Summary",
  );

  const setActiveTab = (tab) => {
    setActiveTabState(tab);
    localStorage.setItem("public_resultados_activeTab", tab);
  };


  /* ==========================================================
     PATHWAYS
     ========================================================== */

  const [pathways, setPathways] = useState([]);
  const [loadingPathways, setLoadingPathways] = useState(false);

  const [selectedPathwayId, setSelectedPathwayIdState] = useState(
    () =>
      localStorage.getItem("public_resultados_selectedPathwayId") || "",
  );

  const setSelectedPathwayId = (id) => {
    const value = id || "";

    setSelectedPathwayIdState(value);

    localStorage.setItem(
      "public_resultados_selectedPathwayId",
      value,
    );
  };


  /* ==========================================================
     RESULTS
     ========================================================== */

  const [resultsData, setResultsData] = useState(null);
  const [loadingResults, setLoadingResults] = useState(false);


  /* ==========================================================
     SECTOR SUB TAB
     ========================================================== */

  const [activeSubTab, setActiveSubTabState] = useState(
    () =>
      localStorage.getItem("public_resultados_activeSubTab") ||
      "Food",
  );

  const setActiveSubTab = (tab) => {
    setActiveSubTabState(tab);

    localStorage.setItem(
      "public_resultados_activeSubTab",
      tab,
    );
  };


  /* ==========================================================
     LOAD PUBLIC PATHWAYS
     ========================================================== */

  useEffect(() => {
    const controller = new AbortController();

    const fetchPublicPathways = async () => {
      try {
        setLoadingPathways(true);

        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/api/results/public/pathways`,
          {
            method: "GET",

            headers: {
              "Content-Type": "application/json",
            },

            credentials: "include",

            signal: controller.signal,
          },
        );

        const data = await res.json().catch(() => ({}));

        if (!res.ok) {
          throw new Error(
            data?.error ||
              `Error loading public pathways: ${res.status}`,
          );
        }

        const list = data.pathways || [];

        setPathways(list);

        /*
         * If there is no stored pathway,
         * automatically select the first public pathway.
         */
        if (list.length > 0 && !selectedPathwayId) {
          setSelectedPathwayId(String(list[0].id));
        }
      } catch (err) {
        if (err.name === "AbortError") return;

        console.error(
          "Error fetching public pathways:",
          err,
        );
      } finally {
        setLoadingPathways(false);
      }
    };

    fetchPublicPathways();

    return () => {
      controller.abort();
    };
  }, [selectedPathwayId]);


  /* ==========================================================
     LOAD PUBLIC RESULTS
     ========================================================== */

  useEffect(() => {
    if (!selectedPathwayId) {
      setResultsData(null);
      return;
    }

    const controller = new AbortController();

    const fetchPublicResults = async () => {
      try {
        setLoadingResults(true);

        const params = new URLSearchParams({
          pathwayId: selectedPathwayId,
        });

        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/api/results/public/results?${params.toString()}`,
          {
            method: "GET",

            headers: {
              "Content-Type": "application/json",
            },

            credentials: "include",

            signal: controller.signal,
          },
        );

        const data = await res.json().catch(() => ({}));

        if (!res.ok) {
          throw new Error(
            data?.error ||
              `Error loading public results: ${res.status}`,
          );
        }

        setResultsData(data);
      } catch (err) {
        if (err.name === "AbortError") return;

        console.error(
          "Error fetching public results:",
          err,
        );

        setResultsData(null);
      } finally {
        setLoadingResults(false);
      }
    };

    fetchPublicResults();

    return () => {
      controller.abort();
    };
  }, [selectedPathwayId]);


  /* ==========================================================
     NORMALIZE RESULTS
     ========================================================== */

  const resultsObj = useMemo(() => {
    if (!resultsData) {
      return null;
    }

    const rawResults = Array.isArray(resultsData)
      ? resultsData[0] ?? null
      : resultsData;

    if (!rawResults) {
      return null;
    }

    /*
     * Clone the object instead of mutating
     * the original API response.
     */
    const normalizedResults = {
      ...rawResults,
    };

    const firstEntry =
      Object.entries(normalizedResults)[0] ?? [];

    const [pathwayName, pathwayObject] = firstEntry;

    if (!pathwayName || !pathwayObject) {
      return normalizedResults;
    }

    normalizedResults[pathwayName] = {
      ...pathwayObject,

      id: selectedPathwayId,

      scenarios: Array.isArray(pathwayObject.scenarios)
        ? pathwayObject.scenarios.map((scenario) => ({
            ...scenario,

            label:
              scenario.label ??
              scenario.id,
          }))
        : pathwayObject.scenarios,
    };

    return normalizedResults;
  }, [resultsData, selectedPathwayId]);


  /* ==========================================================
     SELECT OPTIONS
     ========================================================== */

  const pathwayOptions = useMemo(
    () =>
      pathways.map((pathway) => ({
        value: String(pathway.id),
        label: pathway.name,
      })),
    [pathways],
  );


  /* ==========================================================
     INITIAL LOADING
     ========================================================== */

  if (loadingPathways && pathways.length === 0) {
    return (
      <div
        className="
          flex
          min-h-screen
          w-full
          items-center
          justify-center
          bg-[#fafaf0]
          font-montserrat
        "
      >
        <div className="flex flex-col items-center gap-4">
          <span
            className="
              h-8
              w-8
              animate-spin
              rounded-full
              border-[3px]
              border-slate-200
              border-t-[#08725b]
            "
          />

          <span className="text-sm font-medium text-slate-500">
            Loading public pathways...
          </span>
        </div>
      </div>
    );
  }


  /* ==========================================================
     RENDER
     ========================================================== */

  return (
    <div
      className="
        min-h-screen
        bg-white
        font-montserrat
        text-slate-900
      "
    >
      {/* ======================================================
          HERO
          ====================================================== */}

      <section
        className="
          relative
          overflow-hidden
          border-b
          border-slate-200
          bg-[#fafaf0]
        "
      >
        {/* ----------------------------------------------
            BACKGROUND DECORATION
            ---------------------------------------------- */}

        <div
          className="
            pointer-events-none
            absolute
            inset-0

            bg-[radial-gradient(circle_at_70%_20%,rgba(187,219,170,0.30),transparent_35%),radial-gradient(circle_at_10%_70%,rgba(255,246,193,0.42),transparent_35%)]
          "
        />

        {/* ----------------------------------------------
            HERO CONTENT
            ---------------------------------------------- */}

        <div
          className="
            relative
            mx-auto
            grid
            min-h-[230px]
            w-full
            max-w-[1600px]
            grid-cols-1
            items-center
            gap-10
            px-6
            py-12

            md:px-10
            md:py-14

            xl:grid-cols-[80%_20%]
            xl:px-14
          "
        >
          {/* ============================================
              HERO TEXT
              ============================================ */}

          <div
            className="
              relative
              z-10
            "
          >
            <p
              className="
                text-[12px]
                font-bold
                uppercase
                tracking-[0.22em]
                text-[#08725b]
              "
            >
              Open Dashboard
            </p>

            <h1
              className="
                mt-4

                text-[36px]
                font-black
                leading-[1.08]
                tracking-[-0.035em]
                text-[#07152d]

                sm:text-[42px]

                lg:text-[46px]

                xl:text-[50px]
              "
            >
              Explore pre-established pathways for Ethiopia
            </h1>

            <p
              className="
                mt-5

                text-[16px]
                font-medium
                leading-[1.65]
                text-slate-600

                md:text-[17px]
              "
            >
              Explore public pathway results and understand how
              different assumptions shape Ethiopia&apos;s food,
              land and climate future.
            </p>
          </div>

        </div>
      </section>


      {/* ======================================================
          PATHWAY MENU
          ====================================================== */}

      <PublicPathwayMenu
        activeTab={activeTab}
        onChangeTab={setActiveTab}
        selectedPathwayId={selectedPathwayId}
        onChangePathway={setSelectedPathwayId}
        pathwayOptions={pathwayOptions}
        loadingPathways={loadingPathways}
        loadingResults={loadingResults}
      />


      {/* ======================================================
          PAGE CONTENT
          ====================================================== */}

      <main
        className="
          mx-auto
          w-full
          max-w-[1600px]
          px-6
          py-8

          md:px-10
          md:py-10

          xl:px-14
        "
      >
        {/* ====================================================
            NO RESULTS
            ==================================================== */}

        {!resultsObj &&
          !loadingResults &&
          selectedPathwayId && (
            <div
              className="
                rounded-2xl
                border
                border-slate-200
                bg-[#fafaf7]
                px-6
                py-12
                text-center
              "
            >
              <div
                className="
                  mx-auto
                  flex
                  h-12
                  w-12
                  items-center
                  justify-center
                  rounded-full
                  bg-[#eef5df]
                "
              >
                <span
                  className="
                    text-xl
                    font-bold
                    text-[#478c35]
                  "
                >
                  !
                </span>
              </div>

              <h3
                className="
                  mt-4
                  text-[17px]
                  font-bold
                  text-[#07152d]
                "
              >
                No results available
              </h3>

              <p
                className="
                  mx-auto
                  mt-2
                  max-w-[500px]
                  text-[14px]
                  leading-relaxed
                  text-slate-500
                "
              >
                No public results were found for the selected
                pathway.
              </p>
            </div>
          )}


        {/* ====================================================
            NO PATHWAYS
            ==================================================== */}

        {!selectedPathwayId &&
          !loadingPathways && (
            <div
              className="
                rounded-2xl
                border
                border-slate-200
                bg-[#fafaf7]
                px-6
                py-12
                text-center
              "
            >
              <h3
                className="
                  text-[17px]
                  font-bold
                  text-[#07152d]
                "
              >
                No public pathways available
              </h3>

              <p
                className="
                  mt-2
                  text-[14px]
                  text-slate-500
                "
              >
                Public pathways have not been published yet.
              </p>
            </div>
          )}


        {/* ====================================================
            PATHWAY SUMMARY
            ==================================================== */}

        {resultsObj &&
          activeTab === "Pathway Summary" && (
            <section
              className="
                animate-[fadeIn_0.25s_ease-out]
              "
            >
              <NarrativeContent
                resultsData={resultsObj}
              />
            </section>
          )}


        {/* ====================================================
            SECTOR SPECIFIC RESULTS
            ==================================================== */}

        {resultsObj &&
          activeTab === "Sector Specific Results" && (
            <section
              className="
                animate-[fadeIn_0.25s_ease-out]
              "
            >
              <ResultadoContent
                resultsData={resultsObj}
                activeSubTab={activeSubTab}
                onSubTabChange={setActiveSubTab}
                onSubTabLoadingChange={() => {}}
              />
            </section>
          )}


        {/* ====================================================
            MULTIPLE INDICATORS VIEW

            display:none is intentionally maintained instead
            of conditional mounting because TradeoffContent
            may maintain its own internal state.
            ==================================================== */}

        {resultsObj && (
          <section
            style={{
              display:
                activeTab ===
                "Multiple Indicators View"
                  ? "block"
                  : "none",
            }}
          >
            <TradeoffContent
              resultsData={resultsObj}
              onLoadingChange={() => {}}
            />
          </section>
        )}
      </main>
    </div>
  );
}